import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { colors } from '@src/styles';
import { JsonSchemaFormEditor, RawJsonEditor } from '@src/components';
import { monoFontFamily, monoLabelSX, titleFontFamily } from './canonicalStyles';

interface NewDefinitionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  version: string;
  onVersionChange: (version: string) => void;
  viewMode: 'schema' | 'raw';
  onViewModeChange: (mode: 'schema' | 'raw') => void;
  jsonValue: string;
  onJsonChange: (value: string) => void;
  jsonErrors: string[];
  schema?: object;
  // Optional props for organization selection (used when adding new org to canonical)
  organizationId?: string;
  onOrganizationIdChange?: (id: string) => void;
  organizationsMap?: Record<string, string>;
  existingOrganizationIds?: string[];
  isLoading?: boolean;
}

const VIEW_MODES: { id: 'schema' | 'raw'; label: string }[] = [
  { id: 'schema', label: 'Formulário' },
  { id: 'raw', label: 'JSON' },
];

const inputSX = {
  '& .MuiOutlinedInput-root': {
    height: 38,
    fontSize: '13.5px',
    color: colors.primary.white,
    backgroundColor: colors.background.primary,
    borderRadius: '8px',
    '& fieldset': { borderColor: colors.brand.line },
    '&:hover fieldset': { borderColor: colors.primary.yellow },
    '&.Mui-focused fieldset': { borderColor: colors.primary.yellow },
  },
  '& input::placeholder': { color: colors.brand.textMuted, opacity: 1 },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: colors.brand.textSecondary },
} as const;

const parseFormData = (value: string): { isValid: true; data: object } | { isValid: false } => {
  try {
    return { isValid: true, data: JSON.parse(value) };
  } catch {
    return { isValid: false };
  }
};

const FieldLabel = ({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) => (
  <Typography component="label" htmlFor={htmlFor} sx={{ ...monoLabelSX, display: 'block', marginBottom: 0.75 }}>
    {children}
  </Typography>
);

const NewDefinitionDialog: React.FC<NewDefinitionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  version,
  onVersionChange,
  viewMode,
  onViewModeChange,
  jsonValue,
  onJsonChange,
  jsonErrors,
  schema,
  organizationId,
  onOrganizationIdChange,
  organizationsMap,
  existingOrganizationIds,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasOrganizationField = Boolean(organizationsMap && onOrganizationIdChange);
  const formData = useMemo(() => parseFormData(jsonValue), [jsonValue]);

  // Memoize options to avoid recalculating on every render
  const organizationOptions = useMemo(() => {
    if (!organizationsMap) return [];
    return Object.entries(organizationsMap)
      .filter(([orgId]) => !existingOrganizationIds?.includes(orgId))
      .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' }))
      .map(([orgId, fantasyName]) => ({
        id: orgId,
        label: fantasyName,
      }));
  }, [organizationsMap, existingOrganizationIds]);

  const missingField = hasOrganizationField && !organizationId ? 'organização' : !version.trim() ? 'versão' : null;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="new-definition-title"
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(10, 13, 15, 0.55)' },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: colors.brand.surface,
          backgroundImage: 'none',
          border: isMobile ? 'none' : `1px solid ${colors.brand.line}`,
          borderRadius: isMobile ? 0 : '14px',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isMobile ? '100%' : 'calc(100vh - 48px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          padding: '18px 20px 16px',
          borderBottom: `1px solid ${colors.brand.line}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="new-definition-title"
            component="h2"
            sx={{
              fontFamily: titleFontFamily,
              fontSize: '18px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: colors.primary.white,
            }}
          >
            Nova definição
          </Typography>
          <Typography sx={{ marginTop: '2px', fontSize: '12.5px', color: colors.brand.textSecondary }}>
            Configure esta automação canônica para uma organização.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          aria-label="Fechar"
          sx={{
            width: 32,
            height: 32,
            marginTop: '-2px',
            color: colors.brand.textSecondary,
            '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.25,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {hasOrganizationField && organizationsMap && onOrganizationIdChange && (
            <Box sx={{ flex: '999 1 320px', minWidth: 0 }}>
              <FieldLabel htmlFor="new-definition-organization">Organização</FieldLabel>
              <Autocomplete
                id="new-definition-organization"
                value={
                  organizationId
                    ? {
                        id: organizationId,
                        label: organizationsMap[organizationId] || organizationId,
                      }
                    : null
                }
                onChange={(_event, newValue) => {
                  onOrganizationIdChange(newValue?.id || '');
                }}
                options={organizationOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option.label.toLowerCase().includes(searchTerm) || option.id.toLowerCase().includes(searchTerm),
                  );
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{
                      color: colors.primary.white,
                      '&.MuiAutocomplete-option.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                      '&.MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: 'rgba(255, 160, 21, 0.12)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{option.label}</Typography>
                      <Typography
                        sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}
                      >
                        {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Busque pelo nome ou ID da organização" sx={inputSX} />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      marginTop: 0.5,
                      backgroundColor: colors.brand.surface,
                      backgroundImage: 'none',
                      border: `1px solid ${colors.brand.line}`,
                      borderRadius: '10px',
                      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
                    },
                  },
                  listbox: { sx: { maxHeight: 300, padding: 0.5 } },
                }}
                noOptionsText={
                  <Typography sx={{ fontSize: '13px', color: colors.brand.textSecondary }}>
                    Nenhuma organização encontrada
                  </Typography>
                }
                size="small"
              />
            </Box>
          )}

          <Box sx={{ flex: '1 1 160px', minWidth: 0, maxWidth: { sm: 220 } }}>
            <FieldLabel htmlFor="new-definition-version">Versão</FieldLabel>
            <TextField
              id="new-definition-version"
              fullWidth
              value={version}
              onChange={(event) => onVersionChange(event.target.value)}
              placeholder="1.0.0"
              size="small"
              sx={{ ...inputSX, '& input': { fontFamily: monoFontFamily } }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={monoLabelSX}>Configuração</Typography>
            <Box
              role="tablist"
              aria-label="Modo de edição da configuração"
              sx={{
                display: 'inline-flex',
                padding: '3px',
                borderRadius: '10px',
                backgroundColor: colors.background.primary,
                border: `1px solid ${colors.brand.line}`,
              }}
            >
              {VIEW_MODES.map((mode) => {
                const isActive = viewMode === mode.id;
                return (
                  <Box
                    key={mode.id}
                    component="button"
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onViewModeChange(mode.id)}
                    sx={{
                      height: 28,
                      paddingX: 1.5,
                      border: 'none',
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontFamily: titleFontFamily,
                      fontSize: '12.5px',
                      fontWeight: isActive ? 600 : 500,
                      backgroundColor: isActive ? colors.brand.surface : 'transparent',
                      boxShadow: isActive ? `inset 0 0 0 1px ${colors.brand.line}` : 'none',
                      color: isActive ? colors.primary.white : colors.brand.textSecondary,
                      transition: 'color 0.15s ease, background-color 0.15s ease',
                      '&:hover': { color: colors.primary.white },
                    }}
                  >
                    {mode.label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              height: { xs: '60vh', md: 'min(680px, 62vh)' },
              overflowY: 'auto',
              padding: '12px',
              borderRadius: '10px',
              border: `1px solid ${colors.brand.line}`,
              backgroundColor: colors.background.primary,
            }}
          >
            {viewMode === 'raw' ? (
              <RawJsonEditor value={jsonValue} onChange={onJsonChange} errors={jsonErrors} />
            ) : !schema ? (
              <Typography sx={{ fontSize: '13px', color: colors.brand.textMuted }}>Schema não disponível</Typography>
            ) : !formData.isValid ? (
              <Typography sx={{ fontSize: '13px', color: colors.status.pending }}>
                O JSON atual é inválido. Corrija-o no modo JSON para voltar a usar o formulário.
              </Typography>
            ) : (
              <JsonSchemaFormEditor
                schema={schema}
                formData={formData.data}
                onChange={onJsonChange}
                organizationId={organizationId || undefined}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.25,
          padding: '12px 20px',
          borderTop: `1px solid ${colors.brand.line}`,
          flexShrink: 0,
        }}
      >
        {missingField && (
          <Typography sx={{ fontSize: '12.5px', color: colors.brand.textMuted, marginRight: 'auto' }}>
            Informe a {missingField} para criar a definição.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.25, marginLeft: 'auto' }}>
          <Button
            onClick={onClose}
            disabled={isLoading}
            sx={{
              height: 36,
              paddingX: 2,
              borderRadius: '8px',
              border: `1px solid ${colors.brand.line}`,
              color: colors.brand.textStrong,
              fontFamily: titleFontFamily,
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { borderColor: colors.brand.textMuted, backgroundColor: 'transparent' },
              '&.Mui-disabled': { opacity: 0.5, color: colors.brand.textMuted },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isLoading || Boolean(missingField)}
            sx={{
              height: 36,
              minWidth: 148,
              paddingX: 2,
              borderRadius: '8px',
              backgroundColor: colors.primary.yellow,
              color: colors.brand.ink,
              fontFamily: titleFontFamily,
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#FFB94A' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255, 160, 21, 0.25)', color: 'rgba(10, 13, 15, 0.6)' },
            }}
          >
            {isLoading ? <CircularProgress size={18} sx={{ color: colors.brand.ink }} /> : 'Criar definição'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NewDefinitionDialog;
